import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid
} from '@mui/material';
import {
  Assignment as AssignIcon,
  Preview as PreviewIcon,
  Assessment as StatsIcon,
  Person as PersonIcon,
  Work as JobIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  PlayArrow as RunIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const AutoAssignmentDialog = ({ open, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [maxDistanceMiles, setMaxDistanceMiles] = useState(30);
  const [dryRun, setDryRun] = useState(true);
  
  // Tabs state
  const [activeTab, setActiveTab] = useState('trigger'); // 'trigger', 'preview', 'stats'

  useEffect(() => {
    if (open) {
      fetchStats();
      setError('');
      setSuccess('');
      setAssignments([]);
    }
  }, [open]);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await fetch('/api/auto-assignment/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }
      
      const result = await response.json();
      setStats(result.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(`Failed to load statistics: ${err.message}`);
    } finally {
      setStatsLoading(false);
    }
  };

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`/api/auto-assignment/preview?maxDistanceMiles=${maxDistanceMiles}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate preview');
      }
      
      const result = await response.json();
      setAssignments(result.data.preview || []);
      setActiveTab('preview');
      
      if (result.data.preview?.length === 0) {
        setSuccess('No assignments would be made with current settings.');
      } else {
        setSuccess(`Preview generated: ${result.data.preview.length} potential assignments found.`);
      }
    } catch (err) {
      console.error('Error generating preview:', err);
      setError(`Failed to generate preview: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch('/api/auto-assignment/process', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          maxDistanceMiles,
          dryRun
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process assignments');
      }
      
      const result = await response.json();
      setAssignments(result.data.assignments || []);
      
      if (dryRun) {
        setSuccess(`Dry run completed: ${result.data.assignments?.length || 0} assignments would be made.`);
      } else {
        setSuccess(`Auto-assignment completed successfully! ${result.data.assignments?.length || 0} jobs assigned.`);
        // Refresh stats after successful assignment
        fetchStats();
      }
      
      setActiveTab('preview');
    } catch (err) {
      console.error('Error processing assignments:', err);
      setError(`Failed to process assignments: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    setAssignments([]);
    setActiveTab('trigger');
    onClose();
  };

  const renderStats = () => {
    if (statsLoading) {
      return (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    
    if (!stats) return null;
    
    return (
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <JobIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6" component="div">
                {stats.unassignedJobs}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Unassigned Jobs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <PersonIcon color="secondary" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6" component="div">
                {stats.eligibleMaids}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Eligible Technicians
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <StarIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6" component="div">
                {stats.completedJobAssignments}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Completed Jobs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <LocationIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6" component="div">
                {stats.maxDistanceMiles} mi
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Max Distance
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderAssignments = () => {
    if (assignments.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          <InfoIcon color="info" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            No assignments to display. Try running a preview or process first.
          </Typography>
        </Box>
      );
    }
    
    return (
      <List>
        {assignments.map((assignment, index) => (
          <Accordion key={index} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                <JobIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {assignment.jobNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  → {assignment.assignedMaid?.name || assignment.wouldAssignMaid?.name}
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  <Chip 
                    size="small" 
                    label={assignment.dryRun ? 'Preview' : 'Assigned'} 
                    color={assignment.dryRun ? 'warning' : 'success'}
                    variant={assignment.dryRun ? 'outlined' : 'filled'}
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Job Details</Typography>
                  <Typography variant="body2">
                    <strong>Job:</strong> {assignment.jobNumber}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Scheduled:</strong> {new Date(assignment.scheduledDate).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Assigned Technician</Typography>
                  {(() => {
                    const maid = assignment.assignedMaid || assignment.wouldAssignMaid;
                    return (
                      <>
                        <Typography variant="body2">
                          <strong>Name:</strong> {maid.name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Score:</strong> {maid.score.toFixed(1)} pts
                        </Typography>
                        <Typography variant="body2">
                          <strong>Completed Jobs:</strong> {maid.completedJobs}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Distance:</strong> {maid.distance.toFixed(1)} miles
                        </Typography>
                      </>
                    );
                  })()}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </List>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignIcon color="primary" />
          <Typography variant="h6" component="span">
            Auto Assignment Manager
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Automatically assign the best-qualified technicians to unassigned jobs
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Statistics Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StatsIcon fontSize="small" />
            System Statistics
          </Typography>
          {renderStats()}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Controls Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Assignment Settings
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Maximum Distance (miles)"
                type="number"
                value={maxDistanceMiles}
                onChange={(e) => setMaxDistanceMiles(Number(e.target.value))}
                inputProps={{ min: 1, max: 100 }}
                helperText="Technicians within this radius will be considered"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      Preview Mode (Dry Run)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Show what would happen without making assignments
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={handlePreview}
            disabled={loading}
            size="large"
          >
            Generate Preview
          </Button>
          
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
            onClick={handleProcess}
            disabled={loading}
            color={dryRun ? 'warning' : 'primary'}
            size="large"
          >
            {loading ? 'Processing...' : (dryRun ? 'Run Dry Run' : 'Process Assignments')}
          </Button>
        </Box>

        {/* Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Results Section */}
        {assignments.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignIcon fontSize="small" />
              Assignment Results ({assignments.length})
            </Typography>
            {renderAssignments()}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoAssignmentDialog;